import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LucideIcon } from "lucide-react";

interface CategoryCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  articleCount: number;
  href: string;
  color: "blue" | "green" | "purple" | "orange" | "pink" | "indigo";
}

const colorMap = {
  blue: {
    bg: "bg-blue-50",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    hoverBorder: "hover:border-blue-200",
  },
  green: {
    bg: "bg-green-50",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    hoverBorder: "hover:border-green-200",
  },
  purple: {
    bg: "bg-purple-50",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    hoverBorder: "hover:border-purple-200",
  },
  orange: {
    bg: "bg-orange-50",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    hoverBorder: "hover:border-orange-200",
  },
  pink: {
    bg: "bg-pink-50",
    iconBg: "bg-pink-100",
    iconColor: "text-pink-600",
    hoverBorder: "hover:border-pink-200",
  },
  indigo: {
    bg: "bg-indigo-50",
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600",
    hoverBorder: "hover:border-indigo-200",
  },
};

export function CategoryCard({
  title,
  description,
  icon: Icon,
  articleCount,
  href,
  color,
}: CategoryCardProps) {
  const colors = colorMap[color];

  return (
    <Link to={href}>
      <motion.div
        whileHover={{ y: -4, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
        className={`group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 ${colors.hoverBorder} h-full cursor-pointer hover:shadow-md`}
      >
        <div
          className={`h-12 w-12 ${colors.iconBg} mb-4 flex items-center justify-center rounded-xl`}
        >
          <Icon className={`h-6 w-6 ${colors.iconColor}`} />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-slate-900 transition-colors group-hover:text-blue-600">
          {title}
        </h3>
        <p className="mb-4 text-sm leading-relaxed text-slate-600">{description}</p>
        <div className="flex items-center text-xs text-slate-500">
          <span>{articleCount} articles</span>
        </div>
      </motion.div>
    </Link>
  );
}
